import * as R from 'ramda';
import * as React from 'react';

import { useDealType } from '../../../../packages/Filters/shared/utils/useDealType';
import { useFiltersContext } from '../../../../packages/Filters/shared/utils/useFiltersContext';
import { FOfferType, offerTypeFromJsonQuery } from '../../../../packages/JsonQuery';
import { useHotelsEnabled } from '../../../common/utils/experiments';
import { OfferTypeSelectModal } from '../../components/OfferTypeSelect';
import { getOfferTypeList } from '../../utils/offerType';

interface IOfferTypeModalContainerProps {
  open: boolean;
  onApplyClick(): void;
  onClose(): void;
}

export function OfferTypeModalContainer({ open, onApplyClick, onClose }: IOfferTypeModalContainerProps) {
  const isHotelsEnabled = useHotelsEnabled();
  const { jsonQuery, onChange } = useFiltersContext();
  const dealType = useDealType();
  const currentOfferType = offerTypeFromJsonQuery(jsonQuery);
  const options = React.useMemo(
    () => getOfferTypeList(dealType, currentOfferType, isHotelsEnabled),
    [dealType, currentOfferType, isHotelsEnabled],
  );
  const selectedOptions = React.useMemo(
    () =>
      R.flatten(options.map(t => t.options))
        .filter(offerType => (currentOfferType & offerType.value) !== 0)
        .map(offerType => offerType.value),
    [options, currentOfferType],
  );

  const onSelect = React.useCallback(
    (nextOfferTypes: FOfferType[]) => {
      const nextOfferType: FOfferType = nextOfferTypes.reduce(
        (previousValue, currentValue) => previousValue | currentValue,
        FOfferType.Unexpected,
      );

      if (
        nextOfferType !== currentOfferType &&
        (nextOfferType !== FOfferType.Flat || (currentOfferType & FOfferType.Flat) === 0)
      ) {
        onChange({ action: 'setOfferType', arguments: [nextOfferType] });
        onApplyClick();
      } else {
        onClose();
      }
    },
    [onChange, currentOfferType],
  );

  return <OfferTypeSelectModal onSelect={onSelect} selected={selectedOptions} options={options} />;
}
